/**
 * @jsxRuntime classic
 * @jsx jsx
 */
/**
 * @jsxFrag
 */
import React, { forwardRef, type RefObject, useCallback } from 'react';

import { cssMap, cx, jsx } from '@compiled/react';

import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { AvatarContext, type AvatarContextProps } from '@atlaskit/avatar';
import { Anchor, Pressable, Text } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';

import { useLevel } from './expandable-menu-item-context';
import { useToggleFlyoutMenuOpen } from './flyout-menu-item-context';
import { MenuListItem } from './menu-list-item';
import type { MenuButtonItemProps, MenuItemCommonProps, MenuLinkItemProps } from './types';

const defaultAvatarValues: AvatarContextProps = {
	size: 'small',
};

const hoverActionsDisplayVar = '--hover-actions-display';
const notchColorVar = '--notch-color';

const styles = cssMap({
	root: {
		boxSizing: 'border-box',
		display: 'grid',
		gridTemplateColumns: 'minmax(0, auto) 1fr repeat(3, minmax(0, auto))',
		alignItems: 'center',
		height: '32px',
		userSelect: 'none',
		borderRadius: token('border.radius.200', '4px'),
		color: token('color.text.subtle'),
		[hoverActionsDisplayVar]: 'none',
		[notchColorVar]: 'transparent',
		'&:hover': {
			background: token('elevation.surface.hovered'),
			[notchColorVar]: token('color.background.neutral.hovered'),
		},
		'&:active': {
			background: token('elevation.surface.pressed'),
		},
		'&:hover, &:focus-within': {
			[hoverActionsDisplayVar]: 'flex',
		},
	},
	selected: {
		background: token('color.background.selected'),
		color: token('color.text.selected'),
		[notchColorVar]: token('color.background.selected.bold'),
		'&:hover': {
			color: token('color.text.selected'),
			background: token('color.background.selected.hovered'),
			[notchColorVar]: token('color.background.selected.bold'),
		},
		'&:active': {
			background: token('color.background.selected.pressed'),
		},
	},
	pressed: {
		background: token('elevation.surface.pressed'),
		[notchColorVar]: token('color.background.neutral.hovered'),
	},
	hasDescription: {
		height: '44px',
	},
	// This button/link is positioned to produce the visual appearance of nested
	// buttons whilst the elements are actually siblings in the DOM structure.
	interactiveElem: {
		display: 'grid',
		// Extend the button to the full width of container
		gridColumn: '1 / -1',
		// Each grid item is placed on the same row and stacks on top of each other.
		gridRow: '1',
		gridTemplateColumns: 'subgrid',
		height: '100%',
		padding: token('space.050'),
		position: 'relative',
		background: 'none',
		borderRadius: token('border.radius.200', '4px'),
		color: token('color.text.subtle'),
		alignItems: 'center',
		textAlign: 'start',
	},
	interactiveElemSelected: {
		color: token('color.text.selected'),
	},
	interactiveElemExtension: {
		position: 'absolute',
		inset: token('space.0'),
	},
	notch: {
		position: 'absolute',
		insetBlockStart: '50%',
		insetInlineStart: token('space.0'),
		width: '2px',
		height: '12px',
		transform: 'translateY(-50%)',
		backgroundColor: `var(${notchColorVar})`,
	},
	actions: {
		display: 'flex',
		alignItems: 'center',
		gap: token('space.050'),
		gridRow: '1',
		gridColumn: '5',
		paddingInlineEnd: token('space.050'),
	},
	actionsOnHover: {
		display: `var(${hoverActionsDisplayVar})`,
		gridRow: '1',
		gridColumn: '3',
		alignItems: 'center',
		gap: token('space.050'),
		paddingInlineEnd: token('space.050'),
	},
	textWrapper: {
		gridColumn: '2',
		paddingInlineEnd: token('space.050'),
	},
	textWrapperSpacing: {
		paddingInlineStart: token('space.050'),
	},
	textWrapperLargeSpacing: {
		paddingInlineStart: token('space.075'),
	},
	elemBefore: {
		gridColumn: '1',
		gridRow: '1',
		display: 'flex',
		flexShrink: 0,
		width: '24px',
		height: '24px',
		alignItems: 'center',
		justifyContent: 'center',
		paddingInlineStart: token('space.050'),
	},
	elemAfter: {
		gridColumn: '4',
		gridRow: '1',
		display: 'flex',
		flexShrink: 0,
		height: '24px',
		alignItems: 'center',
		paddingInlineEnd: token('space.050'),
	},
});

type MenuItemBaseProps = MenuItemCommonProps & {
	href?: string;
	target?: HTMLAnchorElement['target'];
	isDisabled?: boolean;
	isSelected?: boolean;
	ariaControls?: string;
	ariaExpanded?: boolean;
	ariaHasPopup?: boolean;
};

/**
 * __MenuItemBase__
 *
 * The base menu item component used to compose MenuButtonItem and MenuLinkItem.
 */
export const MenuItemBase = forwardRef<HTMLAnchorElement | HTMLButtonElement, MenuItemBaseProps>(
	(
		{
			actions,
			actionsOnHover,
			children,
			description,
			elemAfter,
			elemBefore,
			href,
			target,
			isDisabled,
			isSelected,
			onClick,
			ariaControls,
			ariaExpanded,
			ariaHasPopup,
		},
		forwardedRef,
	) => {
		const level = useLevel();
		const toggleFlyoutMenuOpen = useToggleFlyoutMenuOpen();
		const isLink = typeof href !== 'undefined';

		const handleClick = useCallback(
			(
				event: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>,
				analyticsEvent: UIAnalyticsEvent,
			) => {
				onClick?.(event, analyticsEvent);
				// Toggle flyout menu open state when inside a flyout context provider
				toggleFlyoutMenuOpen((isOpen) => !isOpen);
			},
			[onClick, toggleFlyoutMenuOpen],
		);

		const buttonAndAnchorSharedProps = {
			'aria-controls': ariaControls,
			'aria-haspopup': ariaHasPopup,
			onClick: handleClick,
		};

		const interactiveElemContent = (
			<>
				<div
					css={styles.interactiveElemExtension}
					// This extends the clickable area of nested menu items to the width
					// of the root level menu items, while being visually indented.
					style={{ insetInlineStart: `-${level * 26}px` }}
					aria-hidden="true"
				/>
				<div
					css={[
						styles.textWrapper,
						elemBefore && styles.textWrapperSpacing,
						!elemBefore && styles.textWrapperLargeSpacing,
					]}
				>
					<Text
						weight="medium"
						maxLines={1}
						color={isSelected ? 'color.text.selected' : 'color.text.subtle'}
					>
						{children}
					</Text>
					{description && (
						<Text color="color.text.subtlest" size="small" maxLines={1}>
							{description}
						</Text>
					)}
				</div>
			</>
		);
		return (
			<AvatarContext.Provider value={defaultAvatarValues}>
				<div
					css={[
						styles.root,
						isSelected && styles.selected,
						description && styles.hasDescription,
						ariaHasPopup && ariaExpanded && styles.pressed,
					]}
				>
					{isLink ? (
						<Anchor
							{...buttonAndAnchorSharedProps}
							xcss={cx(styles.interactiveElem, isSelected && styles.interactiveElemSelected)}
							// Needed to override Anchor style due to a compiled/emotion conflict
							// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
							style={{ textDecoration: 'none' }}
							aria-current={isSelected ? 'page' : undefined}
							href={href}
							target={target}
							ref={forwardedRef as RefObject<HTMLAnchorElement>}
						>
							<div css={styles.notch} aria-hidden="true" />
							{interactiveElemContent}
						</Anchor>
					) : (
						<Pressable
							{...buttonAndAnchorSharedProps}
							xcss={cx(styles.interactiveElem, isSelected && styles.interactiveElemSelected)}
							aria-expanded={ariaExpanded}
							isDisabled={isDisabled}
							ref={forwardedRef as RefObject<HTMLButtonElement>}
						>
							{interactiveElemContent}
						</Pressable>
					)}
					{elemBefore && <div css={styles.elemBefore}>{elemBefore}</div>}
					{actionsOnHover && <div css={styles.actionsOnHover}>{actionsOnHover}</div>}
					{elemAfter && <div css={styles.elemAfter}>{elemAfter}</div>}
					{actions && <div css={styles.actions}>{actions}</div>}
				</div>
			</AvatarContext.Provider>
		);
	},
);

/**
 * __MenuButtonItem__
 *
 * A menu item button. It should be used within a `ul`.
 */
export const MenuButtonItem = forwardRef<HTMLButtonElement, MenuButtonItemProps>(
	(
		{ actions, children, description, elemAfter, isDisabled, elemBefore, actionsOnHover, onClick },
		forwardedRef,
	) => (
		<MenuListItem>
			<MenuItemBase
				description={description}
				actions={actions}
				elemAfter={elemAfter}
				elemBefore={elemBefore}
				isDisabled={isDisabled}
				actionsOnHover={actionsOnHover}
				onClick={onClick}
				ref={forwardedRef}
			>
				{children}
			</MenuItemBase>
		</MenuListItem>
	),
);

/**
 * __MenuLinkItem__
 *
 * A menu item link. It should be used within a `ul`.
 */
export const MenuLinkItem = forwardRef<HTMLAnchorElement, MenuLinkItemProps>(
	(
		{
			actions,
			children,
			description,
			elemAfter,
			elemBefore,
			href,
			target,
			actionsOnHover,
			isSelected,
			onClick,
		},
		forwardedRef,
	) => (
		<MenuListItem>
			<MenuItemBase
				description={description}
				actions={actions}
				elemAfter={elemAfter}
				// TODO Always use "open in new window" icon when `openInNewWindow` prop is true
				elemBefore={elemBefore}
				href={href}
				target={target}
				actionsOnHover={actionsOnHover}
				isSelected={isSelected}
				onClick={onClick}
				ref={forwardedRef}
			>
				{children}
			</MenuItemBase>
		</MenuListItem>
	),
);
